import React, { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Routes } from '@Constants/routes';
import Notification from '@Common/Notification';
import { useLoadProjectCoaches } from '@Apis/locationTasks';
import LoadingIndicator from '@Components/Common/LoadingIndicator/LoadingIndicator';
import MultiSelectBox from '@Components/Common/MultiSelectBox/MultiSelectBox';
import { LocationTaskDetails } from './Types';
import { isRegularUser } from '../../../../helpers/accountHelper';
import './Data.scss';

interface DataProps {
    taskDetails: LocationTaskDetails | null;
    setLocationTaskDetails: React.Dispatch<React.SetStateAction<LocationTaskDetails | null>>;
    isProjectWidgetActive: boolean;
    isMobile: boolean;
}

const Data = (props: DataProps): ReactElement => {

    const [isEditingProjectCoach, setIsEditingProjectCoach] = useState<boolean>(false);
    const [projectCoaches, setProjectCoaches] = useState<Array<any>>([]);

    const { customerRole } = useSelector((state: any) => state.user);

    const taskId = props.taskDetails?.locationTask.id ?? 0;
    const { data: coachesData, isLoading: isLoadingProjectCoaches, isError: isProjectCoachesError } = useLoadProjectCoaches(taskId, {
        enabled: isEditingProjectCoach && taskId > 0,
    });

    useEffect(() => {
        if (coachesData?.projectCoaches) {
            setProjectCoaches(coachesData.projectCoaches);
        }
    }, [coachesData]);

    useEffect(() => {
        if (isProjectCoachesError) {
            setIsEditingProjectCoach(false);
            Notification('error', 'Error', 'Unable to load project coaches');
        }
    }, [isProjectCoachesError]);

    const toggleEditProjectCoach = (e) => {
        e.preventDefault();
        setIsEditingProjectCoach(true);
    };

    const onProjectCoachChange = (value, name) => {
        const coachId = value === '' || value == null ? null : parseInt(value, 10);
        const selectedCoach = projectCoaches.find(c => c.id === coachId);
        props.setLocationTaskDetails((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                projectCoachId: coachId,
                projectCoach: selectedCoach?.name ?? prev.projectCoach,
            };
        });

        setIsEditingProjectCoach(false);

    };

    return (
        <div className='location-task-data'>
            <div className='header'>
                Data
            </div>
            <div className='body'>
                <div className='row'>
                    <div className='col-12 col-md-4 data-header'>Project Name</div>
                    <div className='col-12 col-md-4 data-header'>Location</div>
                    <div className='col-12 col-md-4 data-header'>{isEditingProjectCoach !== true && 'Project Coach'}</div>
                </div>
                <div className='row mb-3'>
                    <div className='col-12 col-md-4 data-value'>{props.taskDetails?.projectName}</div>
                    <div className='col-12 col-md-4 data-value'>{props.taskDetails?.locationName}{props.taskDetails?.locationAbbreviation ? ` (${props.taskDetails.locationAbbreviation})` : ''}</div>
                    <div className='col-12 col-md-4 data-value'>
                        {props.taskDetails?.projectName && props.isProjectWidgetActive === true && (
                            <>
                                {isEditingProjectCoach === true ? (
                                    <LoadingIndicator isLoading={isLoadingProjectCoaches}>
                                        <MultiSelectBox
                                            name='projectCoachId'
                                            label='Project Coach'
                                            idField='id'
                                            valueField='name'
                                            options={projectCoaches}
                                            selectedValues={(props.taskDetails?.projectCoachId ?? '').toString()}
                                            onChange={onProjectCoachChange}
                                            isRequired={props.taskDetails?.projectName && props.isProjectWidgetActive === true}
                                            isMulti={false}
                                            className='project-coach-select'
                                        />
                                    </LoadingIndicator>
                                ) : (
                                    <div className='project-coach-button'>
                                        {props.taskDetails?.projectCoach ? (
                                            <>
                                                {props.taskDetails?.projectCoach}
                                                <button className='btn btn-link' onClick={toggleEditProjectCoach}>
                                                    Change
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                Not Assigned
                                                <button className='btn btn-link' onClick={toggleEditProjectCoach}>
                                                    Assign
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div className='row'>
                    <div className='col-12 col-md-4 data-header'>Department</div>
                    <div className='col-12 col-md-4 data-header'>Community</div>
                    <div className='col-12 col-md-4 data-header'>Phase</div>
                </div>
                <div className='row mb-3'>
                    <div className='col-12 col-md-4 data-value'>{props.taskDetails?.departmentName}</div>
                    <div className='col-12 col-md-4 data-value'>{props.taskDetails?.communityName}</div>
                    <div className='col-12 col-md-4 data-value'>{props.taskDetails?.phaseName}</div>
                </div>
                <div className='row'>
                    <div className='col-12 col-md-4 data-header'>Location Task ID</div>
                    <div className='col-12 col-md-4 data-header'>Baseline Task ID</div>
                    <div className='col-12 col-md-4 data-header'></div>
                </div>
                <div className='row mb-3'>
                    <div className='col-12 col-md-4 data-value'>{props.taskDetails?.locationTask.id}</div>
                    <div className='col-12 col-md-4 data-value'>
                        {props.taskDetails?.baselineId &&
                            props.taskDetails?.locationTask.baselineTaskId &&
                            props.isMobile === false &&
                            !isRegularUser(customerRole)
                            ? (
                                <a
                                    href={Routes.BaselineTaskList + '/?bid=' + props.taskDetails?.baselineId + '&btid=' + props.taskDetails?.locationTask.baselineTaskId}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='baseline-task-link'>
                                    <span>{props.taskDetails?.locationTask.baselineTaskId}</span>
                                </a>
                            )
                            : null
                        }
                    </div>
                    <div className='col-12 col-md-4 data-value'></div>
                </div>
            </div>
        </div>
    );
};

export default Data;
